/**
 * Linked Options AJAX Navigation
 * In-page product swap for custom.extra_options links - swaps the
 * main-product section without a full page reload.
 */

(function() {
  'use strict';

  var isLoading = false;
  var abortController = null;
  /* Monotonic navigation identity. AbortController stops ordinary fetches,
     but a prefetched response promise cannot be aborted. The identity check
     prevents a slower, older prefetched response from overwriting the most
     recent selector choice during rapid taps. */
  var navigationSequence = 0;
  var overlay = null;
  /** Warm cache when user hovers another linked product (faster swap, feels less like navigation). */
  var prefetchedUrls = {};
  /** False until window load; avoids handling spurious popstate during initial paint (see handlePopState). */
  var popstateReady = false;
  /** True after this document has performed an in-page product navigation.
      Lets Back return to the original state===null history entry safely. */
  var hasAjaxNavigation = false;
  var reviewBackdropDialog = null;
  var reviewBackdropPointerDown = false;

  function isReviewBackdropEvent(event) {
    var dialog = reviewBackdropDialog;
    if (!dialog || !dialog.open) return false;
    var surface = dialog.querySelector('.cad-change-review__surface') || dialog;
    var rect = surface.getBoundingClientRect();
    return event.clientX < rect.left || event.clientX > rect.right ||
      event.clientY < rect.top || event.clientY > rect.bottom;
  }

  function handleReviewBackdropPointerDown(event) {
    reviewBackdropPointerDown = isReviewBackdropEvent(event);
  }

  function handleReviewBackdropClick(event) {
    var dialog = reviewBackdropDialog;
    var shouldClose = reviewBackdropPointerDown && isReviewBackdropEvent(event);
    reviewBackdropPointerDown = false;
    if (shouldClose && dialog && typeof dialog._cadCancelReview === 'function') {
      dialog._cadCancelReview(event);
    }
  }

  /**
   * Full reload: document is fresh from server - drop client-only linkedOption history state so nothing
   * treats this navigation like an in-app AJAX hop (avoids duplicate fetch / second navigation on refresh).
   */
  try {
    var navEntry = performance.getEntriesByType('navigation')[0];
    if (navEntry && navEntry.type === 'reload' && history.state && history.state.linkedOption) {
      var nextState = Object.assign({}, history.state);
      delete nextState.linkedOption;
      delete nextState.url;
      history.replaceState(Object.keys(nextState).length ? nextState : {}, '', window.location.href);
    }
  } catch (e) { /* noop */ }

  /** Build a PDP skeleton overlay that mirrors product-skeleton-loader.liquid.
      Used in place of a spinner so the variant-change loading state matches
      the initial-load skeleton throughout the theme. */
  function repeatSkeleton(count, className) {
    var markup = '';
    for (var i = 0; i < count; i++) {
      markup += '<div class="skeleton-loader__item ' + className + '"></div>';
    }
    return markup;
  }

  function skeletonIntro(mode) {
    return (
      '<div class="pdp-skeleton-loader__intro pdp-skeleton-loader__intro--' + mode + '">' +
        '<div class="pdp-skeleton-loader__meta">' + repeatSkeleton(3, 'pdp-skeleton-loader__meta-item') + '</div>' +
        '<div class="skeleton-loader__item pdp-skeleton-loader__title-line pdp-skeleton-loader__title-line--long"></div>' +
        '<div class="skeleton-loader__item pdp-skeleton-loader__title-line"></div>' +
        '<div class="skeleton-loader__item pdp-skeleton-loader__title-line pdp-skeleton-loader__title-line--short"></div>' +
        '<div class="skeleton-loader__item pdp-skeleton-loader__rating"></div>' +
      '</div>'
    );
  }

  function skeletonDetails() {
    return (
      '<div class="pdp-skeleton-loader__details">' +
        '<div class="pdp-skeleton-loader__options">' +
          '<div class="skeleton-loader__item pdp-skeleton-loader__delivery-strip"></div>' +
          '<div class="skeleton-loader__item pdp-skeleton-loader__label"></div>' +
          '<div class="skeleton-loader__item pdp-skeleton-loader__variant-block"></div>' +
          '<div class="skeleton-loader__item pdp-skeleton-loader__label pdp-skeleton-loader__label--short"></div>' +
          '<div class="pdp-skeleton-loader__swatches">' + repeatSkeleton(4, 'pdp-skeleton-loader__swatch') + '</div>' +
        '</div>' +
        '<div class="pdp-skeleton-loader__purchase">' +
          '<div class="skeleton-loader__item pdp-skeleton-loader__price-card"></div>' +
          '<div class="skeleton-loader__item pdp-skeleton-loader__delivery-card"></div>' +
          '<div class="skeleton-loader__item pdp-skeleton-loader__cta"></div>' +
        '</div>' +
      '</div>'
    );
  }

  function skeletonSticky() {
    return (
      '<div class="pdp-skeleton-loader__mobile-sticky">' +
        '<div class="skeleton-loader__item pdp-skeleton-loader__sticky-image"></div>' +
        '<div class="pdp-skeleton-loader__sticky-copy"><div class="skeleton-loader__item pdp-skeleton-loader__sticky-line"></div><div class="skeleton-loader__item pdp-skeleton-loader__sticky-price"></div></div>' +
        '<div class="skeleton-loader__item pdp-skeleton-loader__sticky-cta"></div>' +
      '</div>'
    );
  }

  function buildSkeletonMarkup() {
    return (
      '<div class="pdp-skeleton-loader__chrome">' +
        '<div class="pdp-skeleton-loader__topbar">' +
          '<div class="skeleton-loader__item pdp-skeleton-loader__logo"></div>' +
          '<div class="skeleton-loader__item pdp-skeleton-loader__search"></div>' +
          '<div class="pdp-skeleton-loader__actions">' + repeatSkeleton(3, 'pdp-skeleton-loader__action') + '</div>' +
        '</div>' +
        '<div class="pdp-skeleton-loader__nav">' + repeatSkeleton(7, 'pdp-skeleton-loader__nav-item') + '</div>' +
      '</div>' +
      '<div class="pdp-skeleton-loader__container">' +
        '<div class="skeleton-loader__item pdp-skeleton-loader__breadcrumb"></div>' +
        '<div class="pdp-skeleton-loader__grid">' +
          skeletonIntro('mobile') +
          '<div class="pdp-skeleton-loader__media">' +
            '<div class="skeleton-loader__item pdp-skeleton-loader__media-main"></div>' +
            '<div class="skeleton-loader__item pdp-skeleton-loader__dots"></div>' +
            '<div class="pdp-skeleton-loader__thumbs">' + repeatSkeleton(4, 'pdp-skeleton-loader__thumb') + '</div>' +
            '<div class="skeleton-loader__item pdp-skeleton-loader__media-tabs"></div>' +
            '<div class="pdp-skeleton-loader__feature-grid">' + repeatSkeleton(3, 'pdp-skeleton-loader__feature') + '</div>' +
          '</div>' +
          '<div class="pdp-skeleton-loader__info">' +
            skeletonIntro('desktop') +
            skeletonDetails() +
          '</div>' +
        '</div>' +
      '</div>' +
      skeletonSticky()
    );
  }

  function getOverlay() {
    if (overlay) return overlay;

    overlay = document.createElement('div');
    overlay.className = 'lv-loading-overlay pdp-skeleton-loader';
    overlay.setAttribute('aria-hidden', 'true');
    overlay.innerHTML = buildSkeletonMarkup();
    /* Full-screen skeleton overlay matching initial-load PDP skeleton.
       Shown fully opaque with no fade-in so the user sees exactly one
       clean skeleton, not a half-transparent "double flash". */
    overlay.style.cssText =
      'position:fixed;inset:0;z-index:var(--z-overlay, 9999);background:rgb(var(--color-background, 255 255 255));' +
      'overflow-y:auto;overflow-x:hidden;pointer-events:auto;opacity:1';

    return overlay;
  }

  function showOverlay(scopedRoot) {
    var el = getOverlay();
    // Mount at opacity 0 so the skeleton fades IN over ~140ms instead
    // of popping in instantly - matches the fade-out at the end of
    // hideOverlay for a single smooth transition feel.
    if (scopedRoot) {
      el.style.cssText =
        'position:absolute;inset:0;z-index:var(--z-overlay, 9999);background:rgb(var(--color-background, 255 255 255));' +
        'overflow:hidden;pointer-events:auto;opacity:0;transition:opacity 140ms ease-out';
      var pos = window.getComputedStyle(scopedRoot).position;
      if (pos === 'static' || pos === '') {
        scopedRoot.dataset.lvOverlayMadeRelative = '1';
        scopedRoot.style.position = 'relative';
      }
      scopedRoot.appendChild(el);
    } else {
      el.style.cssText =
        'position:fixed;inset:0;z-index:var(--z-overlay, 9999);background:rgb(var(--color-background, 255 255 255));' +
        'overflow-y:auto;overflow-x:hidden;pointer-events:auto;opacity:0;transition:opacity 140ms ease-out';
      document.body.appendChild(el);
    }
    // Trigger the fade-in on the next frame so the browser registers
    // the initial opacity:0 before the transition kicks off.
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        if (el && el.parentNode) el.style.opacity = '1';
      });
    });
  }

  function hideOverlay() {
    if (!overlay) return;
    var el = overlay;
    overlay = null;
    var parent = el.parentNode;
    if (!parent) return;
    // Fade the skeleton out over ~220ms so the new gallery image
    // reveals smoothly instead of popping into place. Pointer events
    // off immediately so the user can interact during the fade.
    el.style.pointerEvents = 'none';
    el.style.transition = 'opacity 220ms ease-out';
    el.style.opacity = '0';
    var cleanup = function () {
      if (parent.dataset && parent.dataset.lvOverlayMadeRelative) {
        parent.style.position = '';
        delete parent.dataset.lvOverlayMadeRelative;
      }
      if (el.parentNode) el.parentNode.removeChild(el);
    };
    var done = false;
    el.addEventListener('transitionend', function onEnd() {
      if (done) return; done = true;
      el.removeEventListener('transitionend', onEnd);
      cleanup();
    });
    // Safety net in case transitionend doesn't fire (display:none ancestor, etc.)
    setTimeout(function () { if (done) return; done = true; cleanup(); }, 320);
  }

  // Keep replacement feedback brief and respect reduced-motion preferences.
  function swapMotionEnabled() {
    return !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function supportsWebAnimations() {
    return typeof Element !== 'undefined' && typeof Element.prototype.animate === 'function';
  }

  function visibleSwapTargets(root) {
    if (!root) return [];
    var selectors = [
      '.product__header-meta-row',
      '.product-title-disclosure',
      '.product__rating',
      '.product-card--variants',
      '.product-card--price',
      '.product-card--buy-buttons',
      '.product-discount-offers--teaser'
    ];
    return Array.from(root.querySelectorAll(selectors.join(','))).filter(function (el) {
      return el.getClientRects().length && window.getComputedStyle(el).visibility !== 'hidden';
    });
  }

  // Native option changes commit as soon as data is ready. Do not queue an
  // exit animation in front of the DOM update or stagger the decision cards.
  function animateSwapIn(root) {
    if (!swapMotionEnabled() || !supportsWebAnimations()) return;
    var guided = root.hasAttribute('data-family-model-path');
    visibleSwapTargets(root).filter(function (el) {
      return guided || !el.matches('.product-card--variants');
    }).forEach(function (el) {
      var animation = el.animate([{ opacity: 0.88 }, { opacity: 1 }], {
        duration: guided ? 220 : 160, easing: 'ease-out'
      });
      animation.finished.then(function () { animation.cancel(); }).catch(function () {});
    });
  }

  var pendingChoiceCleanup = null;
  function showPendingChoice(root, url) {
    if (root.hasAttribute('data-family-model-path')) return function () {};
    var target = new URL(url, location.origin);
    var choices = Array.from(root.querySelectorAll('.lv-option-item-link[href]')).filter(function (link) {
      return new URL(link.getAttribute('href'), location.origin).pathname === target.pathname;
    });
    var priorBusy = root.getAttribute('aria-busy');
    root.setAttribute('aria-busy', 'true');
    choices.forEach(function (link) { link.setAttribute('data-linked-pending', ''); });
    // While the displayed product is still the old one, never let a pending
    // choice accidentally purchase it. Purchase.apply owns the committed state.
    var buttons = Array.from(document.querySelectorAll(
      '[id^="MainProduct-"] form[data-type="add-to-cart-form"] button[type="submit"], #sticky-cart-btn'
    )).map(function (button) { var wasDisabled = button.disabled; button.disabled = true; return {button: button, disabled: wasDisabled}; });
    return function (committed) {
      if (priorBusy === null) root.removeAttribute('aria-busy');
      else root.setAttribute('aria-busy', priorBusy);
      choices.forEach(function (link) { link.removeAttribute('data-linked-pending'); });
      if (!committed) buttons.forEach(function (entry) {
        if (entry.button.isConnected) entry.button.disabled = entry.disabled;
      });
    };
  }

  /* In-memory cache of prefetched HTML, keyed by absolute URL.
     Shopify product pages don't ship Cache-Control headers, so the
     browser's HTTP cache won't dedupe a prefetch with the actual
     fetch on click - both go to the network. By stashing the
     response text here we make the click swap effectively
     fast once prefetch has resolved. Responses expire after 15 seconds
     and are bounded to 12 entries, since section HTML can be large
     and price/availability must not stay cached for the whole visit. */
  var prefetchCache = {};

  /* Fetch only the main-product section. The old flow downloaded and parsed
     the entire product document for every option hop even though only this
     section is swapped. Shopify's Section Rendering API keeps the response
     substantially smaller while preserving the same server-rendered Liquid. */
  function sectionRequestUrl(href) {
    var main = document.querySelector('[id^="MainProduct-"]');
    var sectionId = main && main.getAttribute('data-section');
    var url = new URL(href, location.origin);
    if (sectionId) url.searchParams.set('section_id', sectionId);
    return url.href;
  }

  function fetchLinkedPage(href, options) {
    if (window.AlAsilFamilyPage && window.AlAsilFamilyPage.handles(href)) {
      return window.AlAsilFamilyPage.fetch(href, options);
    }
    return fetch(sectionRequestUrl(href), options);
  }

  var TRACKING_PARAMETER_PATTERN = /^(utm_|gclid$|gclsrc$|gad_source$|wbraid$|gbraid$|dclid$|fbclid$|ttclid$|msclkid$|sccid$)/;

  /* A linked-option swap behaves like a variant change even though each
     choice is backed by a separate Shopify product. Keep the acquisition
     parameters from the landing URL in browser history, but do not carry
     product-specific or collection-filter parameters into the next PDP. */
  function withCurrentTracking(href) {
    try {
      var target = new URL(href, location.origin);
      var current = new URL(location.href);
      current.searchParams.forEach(function (value, key) {
        if (TRACKING_PARAMETER_PATTERN.test(key) && !target.searchParams.has(key)) {
          target.searchParams.append(key, value);
        }
      });
      return target.origin === location.origin
        ? target.pathname + target.search + target.hash
        : target.href;
    } catch (error) {
      return href;
    }
  }

  function prefetchLinkedProduct(href) {
    if (!href) return;
    var u = new URL(withCurrentTracking(href), location.origin).href;
    if (prefetchedUrls[u] && Date.now() - prefetchedUrls[u].started < 15000) return;
    if (prefetchedUrls[u]) prefetchedUrls[u].controller.abort();
    var controller = new AbortController();
    var entry = { started: Date.now(), controller: controller };
    prefetchedUrls[u] = entry;
    var timeout = setTimeout(function () { controller.abort(); }, 15000);
    prefetchCache[u] = fetchLinkedPage(u, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' }, credentials: 'same-origin', signal: controller.signal
    }).then(function (r) { return r.ok ? r.text() : null; })
      .catch(function () { return null; }).then(function (html) {
        clearTimeout(timeout);
        entry.settled = true;
        if (!html && prefetchedUrls[u] === entry) { delete prefetchedUrls[u]; delete prefetchCache[u]; }
        return html;
      });
    Object.keys(prefetchedUrls).slice(0, -12).forEach(function (key) {
      if (prefetchedUrls[key].controller) prefetchedUrls[key].controller.abort(); delete prefetchedUrls[key]; delete prefetchCache[key];
    });
  }

  function isLinkedNavigation(href) {
    try {
      var url = new URL(href, location.origin);
      if (url.origin !== location.origin) return false;
      if (url.pathname.indexOf('/products/') !== -1) return true;
      var main = document.querySelector('[data-family-model-path]');
      return !!main && /^\/(?:ar\/)?pages\/[^/]+$/.test(url.pathname);
    } catch (_) { return false; }
  }

  /* Hoist the PDP section's <link rel="stylesheet"> tags into <head> on
     load. Shopify renders some PDP CSS (product-extra-options.css,
     component-price.css) INSIDE the main-product section. The first AJAX
     option-swap would otherwise relocate them to <head> mid-swap - and
     moving a stylesheet while innerHTML is being wiped leaves its rules
     momentarily un-applied, flashing a broken layout for ~1s. It only
     happens on the FIRST change because later swaps find the links already
     in <head>. Doing the move here, on load and in isolation (no innerHTML
     churn, with the link's load semantics preserved), means the first swap
     has nothing to relocate. */
  function hoistSectionCss() {
    var section = document.querySelector('[id^="MainProduct-"]');
    if (!section) return;
    var headHrefs = {};
    document.head.querySelectorAll('link[rel="stylesheet"]').forEach(function (l) {
      if (l.href) headHrefs[l.href] = true;
    });
    section.querySelectorAll('link[rel="stylesheet"]').forEach(function (l) {
      if (!l.href) return;
      if (headHrefs[l.href]) { if (l.parentNode) l.parentNode.removeChild(l); return; }
      document.head.appendChild(l);
      headHrefs[l.href] = true;
    });
  }

  function init() {
    window.__alasilLinkedOptionsReady = true;
    hoistSectionCss();
    popstateReady = document.readyState === 'complete';
    window.addEventListener('load', function() {
      popstateReady = true;
    }, false);
    document.addEventListener('click', handleClick, true);
    document.addEventListener('pointerdown', handleReviewBackdropPointerDown, true);
    document.addEventListener('pointercancel', function () {
      reviewBackdropPointerDown = false;
    }, true);
    document.addEventListener('click', handleReviewBackdropClick, true);
    window.addEventListener('popstate', handlePopState);
    // Prefetch only after explicit option intent. Each Section Rendering
    // response can be hundreds of KB, so warming every configuration during
    // idle competes with the PDP hero image and wastes mobile data.
    function prefetchFromEvent(e) {
      if (isLoading || document.querySelector('[data-family-updating]')) return;
      var a = e.target && e.target.closest && e.target.closest('.lv-option-item-link');
      if (!a || !a.getAttribute('href')) return;
      var href = a.getAttribute('href');
      if (!isLinkedNavigation(href)) return;
      prefetchLinkedProduct(href);
    }
    document.addEventListener('pointerenter', prefetchFromEvent, true);
    // Earlier than hover on many devices (touch / intent to click)
    document.addEventListener('pointerdown', prefetchFromEvent, true);
    document.addEventListener('focusin', prefetchFromEvent, true);
    if (window.AlAsilFamilyPage) window.AlAsilFamilyPage.ready(loadProduct);

  }

  function navigateLinkedOption(link, event, options) {
    if (!link) return false;

    if (link.classList.contains('is-disabled') || link.getAttribute('aria-disabled') === 'true') {
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      return false;
    }

    var href = link.getAttribute('href');
    if (!href || href === '#' || !isLinkedNavigation(href)) return false;
    href = withCurrentTracking(href);

    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (isLoading && abortController) abortController.abort();
    var group = link.dataset.cadExtraGroup || '';
    var main = document.querySelector('[id^="MainProduct-"]');
    var current = main && Array.from(main.querySelectorAll('[data-cad-extra-group]')).find(function (candidate) {
      return candidate.dataset.cadExtraGroup === group && (candidate.getAttribute('aria-current') === 'page' || candidate.hasAttribute('data-cad-dropdown-current'));
    });
    options = Object.assign({}, options || {}, { journeyAxis: link.dataset.familyAxis || '', analyticsSelection: {
      option: group, from: current ? current.dataset.cadExtraValue : '', to: link.dataset.cadExtraValue || '',
      from_product_id: main ? main.dataset.analyticsProductId : '',
      fromPrice: main && main.dataset.analyticsPrice !== undefined ? Number(main.dataset.analyticsPrice) : NaN
    } });
    loadProduct(href, options);
    return true;
  }

  function handleClick(e) {
    var link = e.target.closest('.lv-option-item-link');
    if (!link) return;
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

    /* Configuration sheet options are first selected in-place so the
       shopper can compare choices without navigating on every tap. The
       sheet's final CTA marks the chosen anchor for one committed click,
       which then continues through this existing AJAX swap pipeline. */
    if (link.matches('[data-cad-config-option]') && link.dataset.cadConfigCommit !== '1') {
      e.preventDefault();
      return;
    }

    /* Cross-axis choices are candidates until the shopper explicitly accepts
       the review. The app-provided adjustment data stays the source of truth;
       this layer only presents it and then re-enters this same AJAX pipeline. */
    if (link.dataset.cadReviewRequired === 'true') {
      e.preventDefault();
      e.stopPropagation();
      delete link.dataset.cadConfigCommit;
      openChangeReview(link);
      return;
    }
    navigateLinkedOption(link, e);
  }

  function setReviewText(dialog, selector, value) {
    dialog.querySelectorAll(selector).forEach(function (node) {
      node.textContent = value || '';
    });
  }

  function setReviewRowState(row, changed) {
    if (!row) return;
    row.classList.toggle('is-changed', changed);
    row.querySelectorAll('[data-cad-review-changed-badge]').forEach(function (badge) {
      badge.hidden = !changed;
    });
  }

  function openChangeReview(link) {
    var dialog = document.querySelector('[data-cad-change-review]');
    if (!dialog) return;
    dialog._cadCandidate = link;
    setReviewText(dialog, '[data-cad-review-group-label]', link.dataset.cadReviewGroup);
    var familyRows = dialog.querySelector('[data-cad-review-family-rows]');
    var summaryTemplate = link.querySelector('template[data-cad-review-summary]');
    if (familyRows) {
      familyRows.replaceChildren();
      if (summaryTemplate) familyRows.appendChild(summaryTemplate.content.cloneNode(true));
    }
    setReviewText(dialog, '[data-cad-review-current-price-number]', link.dataset.cadReviewCurrentPrice);
    setReviewText(dialog, '[data-cad-review-target-price-number]', link.dataset.cadReviewTargetPrice);
    setReviewText(dialog, '[data-cad-review-current-availability]', link.dataset.cadReviewCurrentAvailability);
    setReviewText(dialog, '[data-cad-review-target-availability]', link.dataset.cadReviewTargetAvailability);
    var priceRow = dialog.querySelector('[data-cad-review-row="price"]');
    setReviewRowState(priceRow, link.dataset.cadReviewCurrentPrice !== link.dataset.cadReviewTargetPrice);
    var availabilityRow = dialog.querySelector('[data-cad-review-row="availability"]');
    setReviewRowState(availabilityRow, link.dataset.cadReviewCurrentAvailability !== link.dataset.cadReviewTargetAvailability);

    if (!dialog.dataset.cadReviewBound) {
      dialog.dataset.cadReviewBound = '1';
      function cancelReview(event) {
        if (event) event.preventDefault();
        dialog.returnValue = 'cancel';
        if (typeof dialog.close === 'function' && dialog.open) dialog.close();
        else dialog.removeAttribute('open');
      }
      dialog._cadCancelReview = cancelReview;
      dialog.querySelectorAll('button[value="cancel"]').forEach(function (button) {
        button.addEventListener('click', cancelReview);
      });
      var acceptButton = dialog.querySelector('[data-cad-review-accept]');
      if (acceptButton) {
        acceptButton.addEventListener('click', function (event) {
          event.preventDefault();
          event.stopPropagation();
          var candidate = dialog._cadCandidate;
          var reviewScrollLock = Number(dialog.dataset.cadReviewScrollLock);
          dialog._cadCandidate = null;
          delete dialog.dataset.cadReviewScrollLock;
          dialog.returnValue = 'default';
          if (typeof dialog.close === 'function' && dialog.open) dialog.close();
          else dialog.removeAttribute('open');
          if (candidate && candidate.isConnected) {
            delete candidate.dataset.cadReviewCommit;
            delete candidate.dataset.cadConfigCommit;
            if (!navigateLinkedOption(candidate, null, { scrollLock: reviewScrollLock })) {
              candidate.focus({ preventScroll: true });
            }
          }
        });
      }
      dialog.addEventListener('cancel', cancelReview);
      dialog.addEventListener('keydown', function (event) {
        if (event.key !== 'Escape') return;
        event.stopPropagation();
        cancelReview(event);
      });
      dialog.addEventListener('close', function () {
        if (reviewBackdropDialog === dialog) reviewBackdropDialog = null;
        var candidate = dialog._cadCandidate;
        dialog._cadCandidate = null;
        delete dialog.dataset.cadReviewScrollLock;
        if (candidate && candidate.isConnected) {
          candidate.focus({ preventScroll: true });
        }
      });
    }

    reviewBackdropDialog = dialog;
    dialog.dataset.cadReviewScrollLock = String(window.scrollY);
    dialog.returnValue = 'cancel';
    if (typeof dialog.showModal === 'function') dialog.showModal();
    else dialog.setAttribute('open', '');
  }

  async function loadProduct(url, options) {
    options = options || {};
    var requestId = ++navigationSequence;
    if (abortController) abortController.abort();
    isLoading = true;
    var requestController = new AbortController();
    abortController = requestController;

    var mainSection = document.querySelector('[id^="MainProduct-"]');
    if (!mainSection) {
      console.warn('Linked option AJAX skipped: main product section is unavailable.');
      document.dispatchEvent(new CustomEvent('linkedOptionsError', { detail: { url: url } }));
      isLoading = false;
      if (abortController === requestController) abortController = null;
      return;
    }

    // A native <dialog> may restore focus/scroll before its close event runs.
    // Review navigation therefore passes the position captured before opening.
    var hasExplicitScrollLock = typeof options.scrollLock === 'number' && isFinite(options.scrollLock);
    var scrollLock = hasExplicitScrollLock ? options.scrollLock : window.scrollY;

    var swapCommitted = false;
    if (pendingChoiceCleanup) pendingChoiceCleanup(false);
    var clearPendingChoice = showPendingChoice(mainSection, url);
    pendingChoiceCleanup = clearPendingChoice;

    try {
      var signal = requestController.signal;
      var ajaxHeaders = { 'X-Requested-With': 'XMLHttpRequest' };
      var absUrl = url.indexOf('http') === 0 ? url : new URL(url, location.origin).href;

      /* Use the prefetched HTML if available - Shopify doesn't send
         Cache-Control, so the browser HTTP cache won't dedupe. */
      Object.keys(prefetchedUrls).forEach(function (key) {
        if (key !== absUrl && (!prefetchedUrls[key].settled || Date.now() - prefetchedUrls[key].started >= 15000)) {
          if (prefetchedUrls[key].controller) prefetchedUrls[key].controller.abort(); delete prefetchedUrls[key]; delete prefetchCache[key];
        }
      });
      var cachedPromise = prefetchedUrls[absUrl] && Date.now() - prefetchedUrls[absUrl].started < 15000 ? prefetchCache[absUrl] : null;
      var fetchPromise = cachedPromise || fetchLinkedPage(url, { signal: signal, headers: ajaxHeaders, credentials: 'same-origin' });

      if (!hasExplicitScrollLock) scrollLock = window.scrollY;

      /* No skeleton - option changes are fast (prefetch) and the
         first-change flash that used to need masking is fixed at the
         root by hoistSectionCss() (PDP section stylesheets are moved to
         <head> on load, so the swap never relocates CSS mid-wipe). The
         gallery dim happens later, only if the new gallery differs. */
      var useSkeleton = false;
      var gallery = mainSection.querySelector('.product__media-wrapper');

      var html;
      if (cachedPromise) {
        html = await cachedPromise;
        if (requestId !== navigationSequence || signal.aborted) return;
        // Cached entries store the text directly; null = prefetch failed.
        if (!html) {
          delete prefetchCache[absUrl];
          delete prefetchedUrls[absUrl];
          // Fall back to a fresh fetch
          var freshResp = await fetchLinkedPage(url, { signal: signal, headers: ajaxHeaders, credentials: 'same-origin' });
          if (!freshResp.ok) throw new Error('Failed to fetch');
          html = await freshResp.text();
        }
      } else {
        var response = await fetchPromise;
        if (!response.ok) throw new Error('Failed to fetch');
        html = await response.text();
      }
      if (requestId !== navigationSequence || signal.aborted) return;
      // Reuse a recently committed response for quick repeat choices/Back.
      if (!cachedPromise || !prefetchedUrls[absUrl]) {
        prefetchedUrls[absUrl] = {started: Date.now(), controller: requestController, settled: true};
        prefetchCache[absUrl] = Promise.resolve(html);
      }
      Object.keys(prefetchedUrls).slice(0, -12).forEach(function (key) {
        delete prefetchedUrls[key]; delete prefetchCache[key];
      });
      if (window.AlAsilFamilyJourney && mainSection.hasAttribute('data-family-model-path')) {
        await window.AlAsilFamilyJourney.whenIdle(signal);
        if (requestId !== navigationSequence || signal.aborted) return;
      }
      var doc = new DOMParser().parseFromString(html, 'text/html');
      if (window.AlAsilFamilyPage && window.AlAsilFamilyPage.handles(url)) window.AlAsilFamilyPage.prepare(doc, url);
      var newSection = doc.querySelector('[id^="MainProduct-"]') || doc.querySelector('section');
      if (!newSection) throw new Error('Section not found');

      /* Preserve a gallery only when the destination is the SAME product and
         every media identity matches. Comparing only the first image caused
         linked products that shared a hero asset to retain stale media IDs,
         thumbnails, wishlist/compare targets and FOMO metadata. */
      var newGallery = newSection.querySelector('.product__media-wrapper');
      /* Compare by Shopify file basename + version, not full src - the
         live DOM has absolute URLs ("http://shop.cdn/.../iphone-white-1.webp?v=...&width=...")
         while parsed HTML has relative paths and different width= params.
         Filename + v= uniquely identifies the source image regardless.
         Must match the LAST /<file>.<ext> in the path, not the first -
         the first / in an absolute URL is the protocol's //, so a naive
         regex captures the domain ("alasil.ae") as the "filename" and
         every URL collides to the same key, making sameGallery always
         true → old gallery preserved across color/storage swaps. */
      function imgKey(img) {
        var src = img && img.getAttribute('src');
        if (!src) return null;
        var m = src.match(/\/([^/?]+\.\w+)(\?[^#]*)?(?:#|$)/);
        if (!m) return src;
        var v = m[2] ? (m[2].match(/[?&](v=[^&#]+)/) || [])[1] || '' : '';
        return m[1] + '?' + v;
      }
      function gallerySignature(root) {
        if (!root) return '';
        return Array.from(root.querySelectorAll('.product__media-list > [data-media-id]')).map(function (item) {
          var image = item.querySelector('img');
          var type = item.querySelector('video') ? 'video' :
            item.querySelector('model-viewer, product-model') ? 'model' :
            item.querySelector('iframe') ? 'external' : 'image';
          return [item.getAttribute('data-media-id') || '', type, imgKey(image) || ''].join('|');
        }).join('||');
      }
      var currentProductUrl = mainSection.getAttribute('data-product-url') || '';
      var nextProductUrl = newSection.getAttribute('data-product-url') || '';
      var currentSignature = gallerySignature(gallery);
      var nextSignature = gallerySignature(newGallery);
      var sameGallery = !!(currentProductUrl && currentProductUrl === nextProductUrl &&
        currentSignature && currentSignature === nextSignature);

      var savedGallery = null;
      if (sameGallery && gallery) {
        // Save the live gallery node - we'll re-graft it into the new
        // section after innerHTML wipe so its DOM (and loaded images)
        // stays untouched.
        savedGallery = gallery;

        /* The linked options are separate single-variant products, not
           variants of one product. Even when two linked products reuse the
           same image, delivery eligibility can differ. Keep the loaded
           gallery, but sync each 60-minute ribbon from the destination
           product's server-rendered gallery before re-grafting it. */
        var liveMediaItems = savedGallery.querySelectorAll('.product__media-item');
        var nextMediaItems = newGallery.querySelectorAll('.product__media-item');
        liveMediaItems.forEach(function (liveItem, index) {
          liveItem.querySelectorAll('.pcard-60min--pdp').forEach(function (badge) {
            badge.remove();
          });

          var nextBadge = nextMediaItems[index] &&
            nextMediaItems[index].querySelector('.pcard-60min--pdp');
          var liveMedia = liveItem.querySelector('.product__media');
          if (nextBadge && liveMedia) {
            liveMedia.appendChild(nextBadge.cloneNode(true));
          }
        });
      }

      function syncSwap() {
        /* A detached HTMLMediaElement can keep playing and a detached model
           viewer can retain WebGL resources. Stop active PDP media before
           replacing a genuinely different gallery. The same-gallery path
           preserves its live node and current state intentionally. */
        if (!savedGallery && typeof window.pauseAllMedia === 'function') {
          window.pauseAllMedia();
        }

        /* Smart Bundle is rendered as a standalone template section, but
           main-product's desktop layout moves that section inside
           MainProduct so it can sit immediately above "About the Product".
           A linked-product navigation replaces MainProduct.innerHTML; without
           preserving the moved section, the bundle is destroyed and cannot
           react to the linkedOptionsLoaded event below. Keep the live wrapper
           across the wipe. Its custom element refreshes recommendations for
           the destination product after linkedOptionsLoaded is dispatched. */
        var savedSmartBundleSection = mainSection.querySelector(
          '[id*="alasil-complementary"]'
        );
        var savedSmartBundleBuilder = savedSmartBundleSection &&
          savedSmartBundleSection.querySelector('smart-bundle-builder');
        if (savedSmartBundleBuilder &&
            typeof savedSmartBundleBuilder.prepareForLinkedProductSwap === 'function') {
          savedSmartBundleBuilder.prepareForLinkedProductSwap();
        }

        /* Section <link rel="stylesheet"> tags are rendered INSIDE the
           main-product section by Shopify (not in <head>). The innerHTML
           swap below would wipe them and the new HTML's copies would be
           re-processed - during which the rules (e.g.
           `.product__media-list.slider.slider--mobile { flex-wrap: nowrap }`)
           are momentarily un-applied. The default `.grid { flex-wrap: wrap }`
           wins for ~1s and the gallery flashes as a wrapped 3-image grid.
           Move the existing links into <head> first (so innerHTML can't
           wipe them), then drop their duplicates from the new HTML. */
        var existingHrefs = {};
        document.querySelectorAll('link[rel="stylesheet"]').forEach(function (link) {
          if (link.href) existingHrefs[link.href] = true;
        });
        mainSection.querySelectorAll('link[rel="stylesheet"]').forEach(function (link) {
          if (link.href) {
            document.head.appendChild(link); // move out so innerHTML can't wipe
            existingHrefs[link.href] = true;
          }
        });
        newSection.querySelectorAll('link[rel="stylesheet"]').forEach(function (link) {
          if (link.href && existingHrefs[link.href]) link.parentNode.removeChild(link);
        });

        /* Delivery card is time-based, not product-based - preserve
           the live nodes across the swap so the running setInterval
           keeps ticking on attached elements (no re-init flash, no
           rebuild work). Stash the merged mobile chip and the
           standalone estimator before innerHTML wipes them, then
           graft them back into the new section in the same DOM
           positions. */
        var savedDelivery = (function () {
          // Deferred dispatch belongs to the selected product. Never graft a
          // previous product's immediate-delivery estimator over preorder data.
          var preorderSelector = '[data-preorder-state]:not([data-preorder-state="none"])';
          if (mainSection.querySelector(preorderSelector) || newSection.querySelector(preorderSelector)) return null;
          var merged = mainSection.querySelector('.cad-mobile-delivery-info');
          var standalone = mainSection.querySelector('[data-delivery-estimator]');
          if (!merged && !standalone) return null;
          var saved = {};
          if (merged) {
            saved.merged = merged;
            // Capture the trust-badge container - we'll find its
            // counterpart in the new HTML by the same selector.
            saved.mergedHostSel = '.icon-with-text--horizontal .icon-with-text__item[data-modal-trigger="delivery-modal"]';
            /* applyMobileDelivery (in main-product.liquid) ALSO moves
               the .delivery-estimator__progress bar into the trust-
               badge <li> as a sibling of .cad-mobile-delivery-info.
               Preserve it too - otherwise the badge shrinks by ~12px
               on AJAX swap (the bar was the missing line), causing
               every element below to reflow. The applyMobileDelivery
               short-circuit (`if (deliveryBadge.querySelector(...
               cad-mobile-delivery-info)) return`) prevents it from
               being moved again after our graft re-attaches the
               saved meta. */
            var hostBefore = mainSection.querySelector(saved.mergedHostSel);
            if (hostBefore) {
              var prog = hostBefore.querySelector(':scope > .delivery-estimator__progress');
              if (prog) saved.progress = prog;
            }
          }
          if (standalone) {
            saved.standalone = standalone.closest('.delivery-estimator-details') || standalone;
            saved.standaloneId = saved.standalone.id || null;
            saved.standaloneClass = saved.standalone.className;
          }
          /* "Payment Options" caption - JS-inserted by
             applyMobilePaymentLabel before .bnpl-cards--mobile.
             Without preserving, AJAX swap removes it; the
             MutationObserver re-runs apply() with an 80ms debounce
             + JS time, so the user sees a ~1s flash where the label
             disappears and reappears. Save + re-insert here. */
          var paymentLabel = mainSection.querySelector('.cad-payment-options-label');
          if (paymentLabel) {
            saved.paymentLabel = paymentLabel;
            // Anchor selector - we'll find the .bnpl-cards--mobile in
            // the new HTML and insert the saved label as its prev sibling.
            saved.paymentLabelHostSel = '.bnpl-cards--mobile';
          }
          return saved;
        })();

        mainSection.innerHTML = newSection.innerHTML;
        Array.from(newSection.attributes).forEach(function(attr) {
          mainSection.setAttribute(attr.name, attr.value);
        });

        if (savedSmartBundleSection) {
          /* Keep the live bundle directly after the newly-rendered main
             product content. Recommendations retain the server-rendered
             information hierarchy instead of being moved before About. */
          mainSection.appendChild(savedSmartBundleSection);
        }

        if (savedGallery) {
          // Replace the freshly-rendered gallery with the live one we
          // saved (same images, but the new node would have triggered
          // image reloads + re-init of media-gallery). Keeps zoom/
          // slider state intact too.
          var freshGallery = mainSection.querySelector('.product__media-wrapper');
          if (freshGallery && freshGallery.parentNode) {
            freshGallery.parentNode.replaceChild(savedGallery, freshGallery);
          }
        }

        if (savedDelivery) {
          if (savedDelivery.standalone) {
            // Replace the new standalone estimator with the old one
            // (carrying its live setInterval). Match by class so it
            // works regardless of section ID rewrites.
            var newStandalone = mainSection.querySelector('.delivery-estimator-details');
            if (newStandalone && newStandalone.parentNode) {
              /* The preserved node belongs to the previous linked product.
                 Copy the destination product's server-rendered delivery
                 inputs before grafting it back; otherwise data-delivery-jet
                 stays stale and the global Jet banner waits for a later
                 observer/timer pass (or shows the wrong state altogether). */
              var deliveryInputPrefixes = [
                'data-delivery-',
                'data-cutoff-',
                'data-timezone-',
                'data-same-day-',
                'data-next-day-',
                'data-no-delivery-',
                'data-order-within-',
                'data-days-'
              ];
              var transientDeliveryAttributes = {
                'data-delivery-ready': true,
                'data-delivery-signature': true,
                'data-delivery-state': true
              };
              var isDeliveryInput = function (name) {
                return deliveryInputPrefixes.some(function (prefix) {
                  return name.indexOf(prefix) === 0;
                }) && !transientDeliveryAttributes[name];
              };
              Array.prototype.slice.call(savedDelivery.standalone.attributes).forEach(function (attribute) {
                if (isDeliveryInput(attribute.name)) savedDelivery.standalone.removeAttribute(attribute.name);
              });
              Array.prototype.slice.call(newStandalone.attributes).forEach(function (attribute) {
                if (isDeliveryInput(attribute.name)) savedDelivery.standalone.setAttribute(attribute.name, attribute.value);
              });
              Object.keys(transientDeliveryAttributes).forEach(function (name) {
                savedDelivery.standalone.removeAttribute(name);
              });
              /* The expanded panel contains product-specific, server-rendered
                 service copy (Jet 1-hour versus standard 4-hour). Keep the
                 live summary/countdown node, but refresh this static body from
                 the destination product so a Jet -> non-Jet swap cannot retain
                 the previous product's promise text. */
              var savedInlinePanel = savedDelivery.standalone.querySelector('.delivery-inline-panel');
              var newInlinePanel = newStandalone.querySelector('.delivery-inline-panel');
              if (savedInlinePanel && savedInlinePanel.parentNode && newInlinePanel) {
                savedInlinePanel.parentNode.replaceChild(newInlinePanel, savedInlinePanel);
              }
              newStandalone.parentNode.replaceChild(savedDelivery.standalone, newStandalone);
            }
          }
          if (savedDelivery.paymentLabel) {
            var bnplHost = mainSection.querySelector(savedDelivery.paymentLabelHostSel);
            if (bnplHost && bnplHost.parentNode) {
              // Drop any duplicate the new HTML may have shipped
              var existingLabel = bnplHost.parentNode.querySelector(':scope > .cad-payment-options-label');
              if (existingLabel) existingLabel.remove();
              bnplHost.parentNode.insertBefore(savedDelivery.paymentLabel, bnplHost);
            }
          }
          if (savedDelivery.merged) {
            var newHost = mainSection.querySelector(savedDelivery.mergedHostSel);
            if (newHost) {
              // Drop any merged content the new HTML may have shipped
              var existingMerged = newHost.querySelector(':scope > .cad-mobile-delivery-info');
              if (existingMerged) existingMerged.remove();
              newHost.appendChild(savedDelivery.merged);
              // Drop any progress bar the new HTML shipped, then graft
              // the preserved one as the last child (same order as
              // applyMobileDelivery so its CSS wraps to its own row).
              if (savedDelivery.progress) {
                var existingProgress = newHost.querySelector(':scope > .delivery-estimator__progress');
                if (existingProgress) existingProgress.remove();
                newHost.appendChild(savedDelivery.progress);
              }
            }
          }
        }

        /* Commit the new product's delivery state in the same synchronous
           swap frame. The header marquee lives outside MainProduct and cannot
           be replaced by Section Rendering, so explicitly refresh every
           shared surface before the skeleton/transition is removed. */
        if (window.AlAsilDeliveryPromise) {
          window.AlAsilDeliveryPromise.updateAll(document);
        }
      }

      // Commit immediately; entry feedback is non-blocking and never moves
      // the picker away from the pointer or thumb.
      var guidedSwap = mainSection.hasAttribute('data-family-model-path');
      var anchorAxis = guidedSwap && window.AlAsilFamilyJourney
        ? mainSection.getAttribute('data-family-scroll-target') : options.journeyAxis;
      var anchorSelector = /^(model|storage|color|version|summary)$/.test(anchorAxis || '')
        ? '[data-family-step="' + anchorAxis + '"]' : null;
      var oldAnchor = guidedSwap && anchorSelector && mainSection.querySelector(anchorSelector);
      var anchorTop = oldAnchor ? oldAnchor.getBoundingClientRect().top : null;
      if (guidedSwap) scrollLock = window.scrollY;
      syncSwap();
      swapCommitted = true;

      /* Breadcrumb and canonical live in layout/theme.liquid (outside the
         main-product section), so a Section Rendering response cannot contain
         replacement nodes for them. Read the destination identity exposed by
         MainProduct instead. This keeps visible navigation, browser history,
         and canonical metadata aligned during the in-page product swap. */
      try {
        var nextProductTitle = newSection.getAttribute('data-product-title');
        var currentBreadcrumb = document.querySelector(
          '.breadcrumb_main .breadcrumb__text--current[aria-current="page"]'
        );
        if (currentBreadcrumb && nextProductTitle) {
          currentBreadcrumb.textContent = nextProductTitle;
        }

        var canonical = document.querySelector('link[rel="canonical"]');
        var nextCanonical = newSection.getAttribute('data-canonical-url');
        if (canonical && nextCanonical) canonical.href = nextCanonical;
      } catch (e) { /* breadcrumb update is non-critical */ }

      /* The skeleton overlay was scoped INSIDE mainSection, so the
         innerHTML swap above wiped it from the DOM. Re-attach it so
         the new (uninitialized, possibly unloaded-image) content stays
         hidden until images and JS settle - otherwise the user sees a
         brief "3 images then 1" flash as the gallery hydrates. */
      if (overlay && overlay.parentNode !== mainSection) {
        // Ensure mainSection is the positioned ancestor again.
        if (mainSection.style.position === 'static' || !mainSection.style.position) {
          mainSection.dataset.lvOverlayMadeRelative = '1';
          mainSection.style.position = 'relative';
        }
        mainSection.appendChild(overlay);
      }

      // Restore scroll immediately
      window.scrollTo(0, scrollLock);

      /* Desktop layout reorganizer (cad-pdp-cols 2-col split). The
         inline IIFE in main-product.liquid exposes its apply() as
         window.__cadApplyPdpCols. We call it SYNCHRONOUSLY right
         after the innerHTML swap, before paint, so the user never
         sees the mobile-stacked intermediate state (gallery
         ballooning, badges falling to the bottom). Without this,
         the layout reorganized only after the MutationObserver
         debounce (~100-300ms) and the user saw chaos. */
      if (typeof window.__cadApplyPdpCols === 'function') {
        try { window.__cadApplyPdpCols(); } catch (_) {}
      }

      /* Metadata is already server-rendered in its final row. This hook now
         synchronizes stock data only; it never re-parents layout nodes. */
      if (typeof window._cadSkuInlinePlace === 'function') {
        try { window._cadSkuInlinePlace(); } catch (_) {}
      }

      window.AlAsilProductPurchase.apply({ html: doc, scope: mainSection });

      // Update URL for deliberate option clicks. Popstate already moved the
      // active history pointer, so replaying it must not push another entry.
      if (!options.skipHistory) {
        if (history.scrollRestoration) history.scrollRestoration = 'manual';
        history.pushState({ linkedOption: true, url: url }, '', url);
        hasAjaxNavigation = true;
      }

      var nextPageTitle = newSection.getAttribute('data-page-title');
      if (nextPageTitle) document.title = nextPageTitle;

      /* Run reinit BEFORE hiding the overlay. The early-hide pattern
         (hide → setTimeout → reinit) is faster on paper but leaves
         the new section in its default HTML state for ~1s while
         scripts catch up - visible as a "Same-Day Delivery 00:00:00"
         flash on the delivery estimator before its update() rewrites
         the text. Running reinit first means the section is fully
         hydrated when the overlay lifts. With the prefetch cache
         making the fetch effectively free, the total swap is ~100–
         200ms - well under the early-hide threshold of perceived lag. */
      reinitComponents(mainSection);

      /* Run after layout re-parenting/re-initialisation so the animation ends
         at the actual final geometry rather than an intermediate stack. */
      animateSwapIn(mainSection);

      // The immediate journey has already arrived. Keep its destination in
      // place through hydration; never restore the original click position.
      var newAnchor = guidedSwap && anchorSelector && mainSection.querySelector(anchorSelector);
      if (newAnchor && anchorTop !== null) scrollLock = window.scrollY + newAnchor.getBoundingClientRect().top - anchorTop;
      window.scrollTo(0, scrollLock);

      // Final scroll enforcement after a brief delay
      // (catches any deferred scroll from re-executed scripts)
      if (!guidedSwap) setTimeout(function () {
        if (requestId === navigationSequence) window.scrollTo(0, scrollLock);
      }, 32);

      if (useSkeleton) {
        hideOverlay();
      } else if (!savedGallery) {
        // Different image was loaded - fade the new gallery back in.
        // When savedGallery is set, the live gallery DOM was preserved
        // (no swap, no dim), so nothing to fade back here.
        var swappedGallery = mainSection.querySelector('.product__media-wrapper');
        if (swappedGallery) {
          swappedGallery.style.transition = swapMotionEnabled() ? 'opacity 180ms ease-out' : 'none';
          swappedGallery.style.opacity = swapMotionEnabled() ? '0.88' : '1';
          requestAnimationFrame(function () {
            requestAnimationFrame(function () {
              swappedGallery.style.opacity = '1';
            });
          });
          setTimeout(function () {
            swappedGallery.style.transition = '';
            swappedGallery.style.opacity = '';
          }, 220);
        }
      }

      // Publish only the successful committed user choice. Aborted fetches,
      // review cancellations and browser Back/Forward are not selections.
      var selection = options.analyticsSelection;
      if (selection && selection.from_product_id !== mainSection.dataset.analyticsProductId && typeof window.publishAlasilEvent === 'function') {
        var selectionEvent = {
          event_name: 'variant_selected', option: selection.option, from: selection.from, to: selection.to,
          from_product_id: selection.from_product_id, product_id: mainSection.dataset.analyticsProductId
        };
        var nextPrice = Number(mainSection.dataset.analyticsPrice);
        if (mainSection.dataset.analyticsPrice !== undefined && Number.isFinite(nextPrice) && Number.isFinite(selection.fromPrice)) {
          selectionEvent.price_delta = (nextPrice - selection.fromPrice) / 100;
        }
        window.publishAlasilEvent('alasil:storefront_interaction', selectionEvent);
      }

      // Dispatch event
      document.dispatchEvent(new CustomEvent('linkedOptionsLoaded', { detail: { url: url } }));

    } catch (err) {
      if (err.name === 'AbortError' || requestId !== navigationSequence) return;
      console.warn('Linked option AJAX failed; keeping the current document:', err);
      hideOverlay();
      visibleSwapTargets(mainSection).forEach(function (element) {
        if (typeof element.getAnimations === 'function') {
          element.getAnimations().forEach(function (animation) { animation.cancel(); });
        }
        element.style.opacity = '';
        element.style.transform = '';
        element.style.transition = '';
        element.style.willChange = '';
      });
      if (swapCommitted && !options.skipHistory && window.location.href !== new URL(url, location.origin).href) {
        history.pushState({ linkedOption: true, url: url }, '', url);
        hasAjaxNavigation = true;
      }
      document.dispatchEvent(new CustomEvent('linkedOptionsError', {
        detail: { url: url, committed: swapCommitted, error: err }
      }));
    } finally {
      if (requestId === navigationSequence) {
        clearPendingChoice(swapCommitted);
        if (pendingChoiceCleanup === clearPendingChoice) pendingChoiceCleanup = null;
        isLoading = false;
        if (abortController === requestController) abortController = null;
      }
    }
  }

  function reinitComponents(container) {
    /* Previously we removed/inserted every custom element to force a
       disconnect→connect lifecycle. That made sense for elements that
       persisted across the swap, but here `mainSection.innerHTML = ...`
       already replaces them with FRESH instances that auto-upgrade
       (constructor + connectedCallback fire as the browser parses).
       The remove/insert was redundant - and for elements that guard
       against double-init (e.g. MediaGallery's `_initialized`) it
       actively broke them: disconnect cleared their listeners, then
       the guarded re-connect returned early before re-binding. */

    container.querySelectorAll('script:not([type="application/json"]):not([type="application/ld+json"])').forEach(function(oldScript) {
      if (oldScript.src) return;
      /* Skip scripts that set up global behavior on document/body (resize
         listeners, MutationObservers, etc.) - they're already active from
         the initial load and would leak/double-fire if re-executed on
         every AJAX swap. Snippets opt in by adding `data-ajax-skip`. */
      if (oldScript.dataset.ajaxSkip != null) return;
      var script = document.createElement('script');
      script.textContent = oldScript.textContent;
      oldScript.replaceWith(script);
    });

    /* Dynamically-inserted external scripts do not execute through innerHTML.
       Most PDP assets are present on the initial page, but product-model.js is
       conditional: an image-only product can link to a product with 3D media.
       Load it once on demand, then refresh Shopify XR for every model-bearing
       destination so its freshly-rendered ProductJSON is consumed. */
    var productModelScript = container.querySelector('script[src*="product-model.js"]');
    if (productModelScript) {
      var refreshProductModel = function () {
        if (window.ProductModel && typeof window.ProductModel.loadShopifyXR === 'function') {
          window.ProductModel.loadShopifyXR();
        }
      };

      if (customElements.get('product-model')) {
        refreshProductModel();
      } else if (!window.__alasilProductModelScriptPromise) {
        window.__alasilProductModelScriptPromise = new Promise(function(resolve, reject) {
          var script = document.createElement('script');
          script.src = productModelScript.src;
          script.onload = function() { refreshProductModel(); resolve(); };
          script.onerror = reject;
          document.head.appendChild(script);
        }).catch(function() {
          window.__alasilProductModelScriptPromise = null;
        });
      } else {
        window.__alasilProductModelScriptPromise.then(refreshProductModel);
      }
    }

    /* No global resize dispatch - CSS-link preservation in syncSwap
       means layout is already final when innerHTML completes. The old
       dispatch triggered every resize-listener on the page (Tabby,
       Tamara, sticky cart, etc.), adding ~100–200ms for nothing. */

    var gallery = container.querySelector('[id^="MediaGallery-"]');
    if (gallery) {
      var firstMedia = gallery.querySelector('[data-media-id]');
      if (firstMedia && gallery.setActiveMedia) {
        gallery.setActiveMedia(firstMedia.dataset.mediaId, false);
      }
    }

    /* The destination product's server-rendered badge is authoritative.
       Rescan any animation placeholders after the AJAX section swap instead
       of carrying the previous product's badge into the new gallery. */
    if (typeof window.rescanLottieBadges === 'function') {
      window.rescanLottieBadges();
    }

    publishVariantChange(container);
  }

  function publishVariantChange(container) {
    if (typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    var variantInput = container.querySelector('input[name="id"]');
    var variantJson = container.querySelector('script[type="application/json"][data-product-variants]');
    if (!variantInput || !variantJson) return;

    try {
      var variants = JSON.parse(variantJson.textContent);
      var variant = variants.find(function(v) { return String(v.id) === variantInput.value; });
      if (variant) {
        publish(PUB_SUB_EVENTS.variantChange, {
          data: { sectionId: container.dataset.section, variant: variant, html: container }
        });
      }
    } catch (e) { /* Silent fail */ }
  }

  function handlePopState(e) {
    // Ignore popstate until load has fired for normal navigations/reloads. BFCache back/forward uses type
    // back_forward and must work without a load event.
    if (!popstateReady && !hasAjaxNavigation) {
      try {
        var nav = performance.getEntriesByType('navigation')[0];
        if (!nav || nav.type !== 'back_forward') return;
      } catch (err) {
        return;
      }
    }

    if (e.state && e.state.linkedOption) {
      loadProduct(e.state.url || window.location.href, { skipHistory: true });
      return;
    }

    /* The first server-rendered product entry naturally has state===null.
       Handle it only after this document has made an AJAX product hop; this
       distinguishes a real Back action from the spurious null popstate some
       browsers emit during initial load. */
    if ((hasAjaxNavigation || document.querySelector('[data-family-model-path]')) && isLinkedNavigation(window.location.href)) {
      loadProduct(window.location.href, { skipHistory: true });
    }
  }

  // Start
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
